import React, { FC, memo } from 'react';
import ScenarioSwitch from '@components/Headers/core/ScenarioSwitch';
import { useProject } from '@context/Project/ProjectProvider';
import { Divider, Stack } from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { AdditionalSettings } from '@views/Project/components/AdditionalSettings/AdditionalSettings';
import ModelFiltersPopover from '@views/Project/components/ModelFiltersPopover';
import AppliedFilterChips from '@views/Project/components/Toolbox/components/AppliedFilterChips';
import DashboardFilters from '@views/Project/components/Toolbox/components/DashboardFilters';
import LinkButton from '@views/Project/components/Toolbox/components/LinkButton';
import ModesDropdown from '@views/Project/components/Toolbox/components/ModesDropdown';
import useToolboxLinks from '@views/Project/components/Toolbox/hooks/useToolboxLinks';
import { LinksWrapper } from '@views/Project/components/Toolbox/Toolbox.styled';

/**
 * The project toolbox row in AC1's order: mode selector, the view tabs, the
 * Filters funnel with the applied-filter chips beside it, then the kebab and
 * the scenario switch on the far right. The funnel is the only entry point to
 * model filtering; the Dashboard view keeps its own filters control in the
 * same slot and its own chips elsewhere, so the model chip row renders only
 * outside Dashboard mode. The left group takes the leftover width and may
 * shrink while the right group never does, so the chip row's container
 * measures exactly the space between the funnel and the kebab and overflows
 * into +N instead of growing the toolbar.
 */
const Toolbox: FC = () => {
  const { isVersionsShown, isDashboardMode } = useProject();
  const isProjectLinksDisabled = isVersionsShown;

  const { toolboxLinks, handleLinkClick } = useToolboxLinks();

  return (
    <Stack
      p={6}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      borderBottom={`1px solid ${themePalette.grey[500]}`}
    >
      <Stack gap={4} direction="row" alignItems="center" flex={1} minWidth={0}>
        <ModesDropdown />

        <Divider orientation="vertical" flexItem />

        <LinksWrapper $disabled={isProjectLinksDisabled}>
          {toolboxLinks.map((linkItem) => (
            <LinkButton
              key={linkItem.label}
              item={linkItem}
              onClick={(e) => handleLinkClick(e, linkItem.to)}
            />
          ))}
        </LinksWrapper>

        <Divider orientation="vertical" flexItem />

        {isDashboardMode ? <DashboardFilters /> : <ModelFiltersPopover />}

        {!isDashboardMode && <AppliedFilterChips />}
      </Stack>

      <Stack gap={4} direction="row" alignItems="center" flexShrink={0}>
        <AdditionalSettings />

        <ScenarioSwitch />
      </Stack>
    </Stack>
  );
};

export default memo(Toolbox);
